import React from 'react'
import { Modal, Button, Alert, Segmented, Spin, Checkbox, theme } from 'antd'
import CostSummary from './CostSummary'
import './RelationshipAnalysisModal.css'

interface RelationshipChapterDetail {
  chapterGuid?: string
  chapterName?: string
  wordCount?: number
  chapterNumber?: number
  freeRunAvailable?: boolean
}

interface RelationshipAnalysisModalProps {
  visible: boolean
  onCancel: () => void
  onConfirm: () => void
  confirmLoading?: boolean
  chaptersToAnalyze?: number | null
  creditCost?: number | null
  chapterDetails?: RelationshipChapterDetail[]
  freeRunAvailable?: boolean
  creditBalance?: number | null
  disableConfirm?: boolean
  insufficientCredits?: boolean
  includeAnalyzed?: boolean
  onIncludeAnalyzedChange?: (value: boolean) => void
  includeAnalyzedDisabled?: boolean
  selectedChapterGuids?: string[]
  onSelectedChapterGuidsChange?: (chapterGuids: string[]) => void
}

const RelationshipAnalysisModal: React.FC<RelationshipAnalysisModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  confirmLoading = false,
  chaptersToAnalyze,
  creditCost,
  chapterDetails = [],
  freeRunAvailable,
  creditBalance,
  disableConfirm = false,
  insufficientCredits = false,
  includeAnalyzed = false,
  onIncludeAnalyzedChange,
  includeAnalyzedDisabled = false,
  selectedChapterGuids = [],
  onSelectedChapterGuidsChange,
}) => {
  const { token } = theme.useToken()

  const selectionEnabled = includeAnalyzed && !!onSelectedChapterGuidsChange
  const sortedChapters = React.useMemo(() => {
    const normalized = [...chapterDetails]
    normalized.sort((a, b) => {
      const numA =
        typeof a.chapterNumber === 'number' && a.chapterNumber > 0 ? a.chapterNumber : Number.POSITIVE_INFINITY
      const numB =
        typeof b.chapterNumber === 'number' && b.chapterNumber > 0 ? b.chapterNumber : Number.POSITIVE_INFINITY
      if (numA !== numB) return numA - numB
      const nameA = (a.chapterName || '').toLowerCase()
      const nameB = (b.chapterName || '').toLowerCase()
      if (nameA && nameB && nameA !== nameB) return nameA.localeCompare(nameB)
      const guidA = a.chapterGuid || ''
      const guidB = b.chapterGuid || ''
      return guidA.localeCompare(guidB)
    })
    return normalized
  }, [chapterDetails])

  const selectedChapterGuidSet = React.useMemo(
    () => new Set(selectedChapterGuids ?? []),
    [selectedChapterGuids]
  )

  const selectedChapters = React.useMemo(() => {
    if (!selectionEnabled) {
      return sortedChapters
    }
    return sortedChapters.filter((chapter) => {
      const guid = chapter.chapterGuid
      return guid ? selectedChapterGuidSet.has(guid) : false
    })
  }, [selectionEnabled, selectedChapterGuidSet, sortedChapters])

  const chapterCount = selectionEnabled
    ? selectedChapters.length
    : typeof chaptersToAnalyze === 'number'
      ? chaptersToAnalyze
      : sortedChapters.length

  const summaryCopy = chapterCount
    ? includeAnalyzed
      ? `Re-run analysis for ${chapterCount} chapter${chapterCount === 1 ? '' : 's'}.`
      : `Analyze ${chapterCount} chapter${chapterCount === 1 ? '' : 's'} with missing relationship data.`
    : includeAnalyzed
      ? 'Select chapters to re-run analysis.'
      : 'Analyze any chapters that are missing relationship data.'
  const perWordRate = 0.1

  const formatCredits = (amount?: number | null) => {
    if (typeof amount !== 'number' || Number.isNaN(amount)) return null
    return `${amount.toLocaleString()} credit${amount === 1 ? '' : 's'}`
  }

  const getChapterCost = (wordCount?: number) => {
    if (typeof wordCount !== 'number' || wordCount <= 0) return null
    return Math.ceil(wordCount * perWordRate)
  }

  const resolvedFreeRunAvailable =
    typeof freeRunAvailable === 'boolean'
      ? freeRunAvailable
      : chapterDetails.some((chapter) => chapter.freeRunAvailable !== false)
  const rerunLocked = resolvedFreeRunAvailable
  const analysisMode = includeAnalyzed ? 'rerun' : 'missing'
  const modeDisabled = includeAnalyzedDisabled || confirmLoading
  const isFullSelection =
    selectionEnabled &&
    sortedChapters.length > 0 &&
    selectedChapters.length === sortedChapters.length
  const showRerunWarning =
    analysisMode === 'rerun' && !resolvedFreeRunAvailable && chapterCount > 0
  const rerunWarningMessage = selectionEnabled
    ? isFullSelection
      ? 'This will reset any manual edits (characters, aliases, and relationship rules) across all chapters once the rerun completes.'
      : 'This will reset any manual edits (characters, aliases, and relationship rules) for the selected chapters once the rerun completes.'
    : 'This will reset any manual edits (characters, aliases, and relationship rules) for the chapters being re-run once the rerun completes.'
  const rerunLockedNotice = rerunLocked
    ? 'Re-run becomes available after the first book-wide run completes.'
    : null

  const chaptersForCost = selectionEnabled ? selectedChapters : sortedChapters
  const chapterStats = React.useMemo(() => {
    return chaptersForCost.reduce(
      (acc, chapter) => {
        const numericWordCount =
          typeof chapter.wordCount === 'number' && chapter.wordCount > 0 ? chapter.wordCount : 0
        const isFreeRun = resolvedFreeRunAvailable && chapter.freeRunAvailable !== false
        if (isFreeRun) {
          acc.freeChapters += 1
        } else {
          acc.paidChapters += 1
          acc.chargeableWordCount += numericWordCount
        }
        acc.totalWordCount += numericWordCount
        return acc
      },
      {
        freeChapters: 0,
        paidChapters: 0,
        chargeableWordCount: 0,
        totalWordCount: 0,
      }
    )
  }, [chaptersForCost, resolvedFreeRunAvailable])

  const computedCreditCost =
    chapterStats.chargeableWordCount > 0
      ? Math.ceil(chapterStats.chargeableWordCount * perWordRate)
      : 0
  const resolvedCreditCost = selectionEnabled
    ? computedCreditCost
    : typeof creditCost === 'number' && Number.isFinite(creditCost)
      ? creditCost
      : computedCreditCost
  const showCostMismatch =
    !selectionEnabled &&
    typeof creditCost === 'number' &&
    Number.isFinite(creditCost) &&
    creditCost !== computedCreditCost
  const listContainerStyles: React.CSSProperties = {
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: 6,
    padding: 12,
    background: token.colorBgLayout,
    maxHeight: 'min(180px, 28vh)',
    overflowY: 'auto',
  }

  const confirmLabel = includeAnalyzed ? 'Re-run analysis' : 'Run analysis'
  const confirmDisabled =
    disableConfirm ||
    confirmLoading ||
    (!includeAnalyzed && chapterCount === 0) ||
    (includeAnalyzed && selectionEnabled && chapterCount === 0)
  const confirmDanger = showRerunWarning
  const tourReady = !confirmLoading
  const [advancedOpen, setAdvancedOpen] = React.useState(false)

  React.useEffect(() => {
    if (!onIncludeAnalyzedChange) {
      return
    }
    if (rerunLocked && includeAnalyzed) {
      onIncludeAnalyzedChange(false)
    }
  }, [includeAnalyzed, onIncludeAnalyzedChange, rerunLocked])

  React.useEffect(() => {
    if (!visible) {
      setAdvancedOpen(false)
    }
  }, [visible])

  const toggleChapterSelection = (chapterGuid?: string, checked?: boolean) => {
    if (!selectionEnabled || !chapterGuid) return
    const nextSelected = new Set(selectedChapterGuidSet)
    if (checked) {
      nextSelected.add(chapterGuid)
    } else {
      nextSelected.delete(chapterGuid)
    }
    onSelectedChapterGuidsChange?.(Array.from(nextSelected))
  }

  const handleSelectAll = () => {
    if (!selectionEnabled) return
    const allGuids = sortedChapters
      .map((chapter) => chapter.chapterGuid)
      .filter((guid): guid is string => !!guid)
    onSelectedChapterGuidsChange?.(allGuids)
  }

  const handleClearAll = () => {
    if (!selectionEnabled) return
    onSelectedChapterGuidsChange?.([])
  }

  const queuedCountLabel = selectionEnabled
    ? `${chapterCount} of ${sortedChapters.length}`
    : `${chapterCount}`

  const ChaptersList = () => (
    <div style={listContainerStyles}>
      {sortedChapters.length === 0 ? (
        <div style={{ color: token.colorTextSecondary }}>All chapters are covered.</div>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {sortedChapters.map((chapter, idx) => {
            const key = chapter.chapterGuid || `${chapter.chapterName}-${idx}` || idx
            const numericWordCount =
              typeof chapter.wordCount === 'number' && chapter.wordCount > 0 ? chapter.wordCount : null
            const wordCountLabel = numericWordCount
              ? `${numericWordCount.toLocaleString()} word${numericWordCount === 1 ? '' : 's'}`
              : null
            const chapterLabel =
              chapter.chapterName ||
              (typeof chapter.chapterNumber === 'number'
                ? `Chapter ${chapter.chapterNumber}`
                : `Chapter ${idx + 1}`)
            const isFreeRun = resolvedFreeRunAvailable && chapter.freeRunAvailable !== false
            const rerunCost = !isFreeRun ? getChapterCost(numericWordCount ?? undefined) : null
            const rerunCostLabel = rerunCost != null ? formatCredits(rerunCost) : null
            const isSelected = selectionEnabled
              ? !!chapter.chapterGuid && selectedChapterGuidSet.has(chapter.chapterGuid)
              : true

            return (
              <li key={key} style={{ marginBottom: 6 }}>
                <div
                  style={{
                    color: token.colorText,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 16,
                    alignItems: 'baseline',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                    {selectionEnabled && (
                      <Checkbox
                        checked={isSelected}
                        disabled={confirmLoading}
                        onChange={(event) => toggleChapterSelection(chapter.chapterGuid, event.target.checked)}
                      />
                    )}
                    <span>
                      {chapterLabel}
                      {wordCountLabel && (
                        <span style={{ color: token.colorTextSecondary }}> · {wordCountLabel}</span>
                      )}
                    </span>
                  </span>
                  <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>
                    {isFreeRun
                      ? 'Free (first book run)'
                      : rerunCostLabel
                        ? `${rerunCostLabel} rerun`
                        : 'Credits apply'}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )

  return (
    <Modal
      title={
        <div className="translation-modal-titlebar">
          <span className="translation-modal-titlebar__title">Analyze Cast & Relationships</span>
        </div>
      }
      visible={visible}
      width={720}
      style={{ top: 40 }}
      onCancel={onCancel}
      closable={!confirmLoading}
      maskClosable={!confirmLoading}
      keyboard={!confirmLoading}
      wrapClassName="characters-modal"
      className="relationship-analysis-modal"
      footer={
        <div className="relationship-analysis-modal-footer">
          <div className="translation-modal-footer-summary" aria-live="polite">
            <span>
              <strong>{chapterCount}</strong> chapter{chapterCount === 1 ? '' : 's'}
            </span>
            <span className="translation-modal-footer-summary__separator">·</span>
            <span>
              <strong>{resolvedCreditCost.toLocaleString()}</strong> credits
            </span>
            <span className="translation-modal-footer-summary__separator">·</span>
            {insufficientCredits ? (
              <span className="translation-modal-footer-summary__warning">Top-up needed</span>
            ) : confirmDisabled ? (
              <span className="translation-modal-footer-summary__warning">Requirements needed</span>
            ) : (
              <span>Ready</span>
            )}
          </div>
          <div className="relationship-analysis-modal-footer__actions">
            <Button
              key="cancel"
              onClick={onCancel}
              disabled={confirmLoading}
              data-testid="tutorial:characters:analysis-cancel"
            >
              Cancel
            </Button>
            <Button
              key="confirm"
              type="primary"
              danger={confirmDanger}
              loading={confirmLoading}
              onClick={onConfirm}
              disabled={confirmDisabled}
              data-testid="tutorial:characters:analysis-start"
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      }
    >
      <Spin spinning={confirmLoading} tip="Starting analysis...">
        <div
          data-tour-id="analysis-modal"
          data-testid="tutorial:characters:analysis-modal"
          data-tour-ready={tourReady ? 'true' : 'false'}
          className="translation-modal-body relationship-analysis-modal-body"
        >
          <section
            data-tour-id="analysis-modal-about"
            data-testid="tutorial:characters:analysis-about"
            data-tour-ready={tourReady ? 'true' : 'false'}
            className="translation-modal-block"
          >
            <div className="translation-modal-block-header">
              <div>
                <span className="translation-modal-kicker">Scope</span>
                <h3>Analyze what’s needed</h3>
                <p>{summaryCopy}</p>
              </div>
              <span className={`relationship-analysis-readiness-pill ${confirmDisabled ? 'is-needed' : 'is-ready'}`}>
                {confirmDisabled ? 'Needed' : 'Ready'}
              </span>
            </div>
            <div className="relationship-analysis-scope-grid">
              <div className="relationship-analysis-scope-row">
                <span>Scope</span>
                <strong>{includeAnalyzed ? 'Selected analyzed chapters' : 'Missing chapter data'}</strong>
              </div>
              <div className="relationship-analysis-scope-row">
                <span>Chapters</span>
                <strong>{queuedCountLabel}</strong>
              </div>
              <div className="relationship-analysis-scope-row">
                <span>Credit cost</span>
                <strong>{resolvedCreditCost.toLocaleString()}</strong>
              </div>
            </div>
          </section>

          <section
            className="translation-modal-block"
            data-tour-id="analysis-modal-cost"
            data-testid="tutorial:characters:analysis-cost"
            data-tour-ready={tourReady ? 'true' : 'false'}
          >
            <div className="translation-modal-block-header">
              <div>
                <span className="translation-modal-kicker">Cost</span>
                <h3>Credit check</h3>
                <p>Credits are only charged when rerunning chapters.</p>
              </div>
            </div>
            <CostSummary
              creditBalance={creditBalance ?? null}
              baseCost={resolvedCreditCost}
              tooltipText="Credits are only charged when rerunning chapters. We sum the rerun word counts, multiply by 0.1, then round up."
              containerStyle={{ marginBottom: showCostMismatch || insufficientCredits ? 12 : 0 }}
              afterCostNode={
                insufficientCredits ? (
                  <span style={{ marginLeft: 10, color: token.colorError, fontWeight: 600 }}>
                    Not enough credits
                  </span>
                ) : chapterStats.paidChapters === 0 && chapterCount > 0 ? (
                  <span style={{ marginLeft: 10, color: token.colorSuccess }}>
                    All chapters in this run are free.
                  </span>
                ) : null
              }
            />
            {showCostMismatch && (
              <Alert
                type="warning"
                showIcon
                message="Updated credit estimate differs from the server’s latest value. Reload if the total looks outdated."
              />
            )}
            {insufficientCredits && (
              <Alert
                type="error"
                message="You don’t have enough credits to run this analysis. Please top up and try again."
                showIcon
              />
            )}
          </section>

          <section className="translation-modal-block relationship-analysis-advanced-block">
            <button
              type="button"
              className="relationship-analysis-advanced-toggle"
              onClick={() => setAdvancedOpen((open) => !open)}
              aria-expanded={advancedOpen}
            >
              <span>Advanced analysis options</span>
              <span>{advancedOpen ? 'Hide' : 'Show'}</span>
            </button>
            {advancedOpen && (
              <div className="relationship-analysis-advanced-content">
                <div className="relationship-analysis-modal-helper">
                  The first full-book run is free and automatically runs before translations if needed.
                </div>
                <div className="relationship-analysis-modal-helper">
                  Reruns cost <strong>0.1 credit per word</strong> in the rerun chapters.
                </div>
                {onIncludeAnalyzedChange && (
                  <div className="relationship-analysis-advanced-section">
                    <div className="translation-modal-block-header">
                      <div>
                        <span className="translation-modal-kicker">Mode</span>
                        <h3>Analysis mode</h3>
                        <p>Run only missing chapters or rerun selected analyzed chapters when available.</p>
                      </div>
                    </div>
                    <Segmented
                      value={analysisMode}
                      onChange={(value) => onIncludeAnalyzedChange(value === 'rerun')}
                      options={[
                        { label: 'Analyze missing chapters', value: 'missing' },
                        { label: 'Re-run analyzed chapters', value: 'rerun', disabled: rerunLocked },
                      ]}
                      disabled={modeDisabled}
                    />
                    {rerunLockedNotice && (
                      <div className="relationship-analysis-modal-helper relationship-analysis-modal-helper--spaced">
                        {rerunLockedNotice}
                      </div>
                    )}
                    {showRerunWarning && (
                      <Alert
                        type="error"
                        showIcon
                        message={rerunWarningMessage}
                        style={{ marginTop: 10 }}
                      />
                    )}
                  </div>
                )}
                <div className="relationship-analysis-advanced-section">
                  <div className="translation-modal-block-header">
                    <div>
                      <span className="translation-modal-kicker">Chapters</span>
                      <h3>Queued for analysis ({queuedCountLabel})</h3>
                      <p>Review the chapters that will receive missing relationship data or be rerun.</p>
                    </div>
                  </div>
                  {selectionEnabled && (
                    <div className="relationship-analysis-selection-actions">
                      <Button
                        size="small"
                        onClick={handleSelectAll}
                        disabled={confirmLoading || sortedChapters.length === 0}
                      >
                        Select all
                      </Button>
                      <Button
                        size="small"
                        onClick={handleClearAll}
                        disabled={confirmLoading || sortedChapters.length === 0}
                      >
                        Clear all
                      </Button>
                    </div>
                  )}
                  <ChaptersList />
                </div>
              </div>
            )}
          </section>
        </div>
      </Spin>
    </Modal>
  )
}

export default RelationshipAnalysisModal
